import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { dateTime } from '../format.ts'

interface Item {
  id: string
  model: string
  catalogCode: string
  quantity: number
  customerOrderItemId: string | null
  forCustomer: string | null
  confirmedDeliveryDate: string | null
  cancelledAt: string | null
  options: string[]
}
interface EmailAttempt {
  id: string
  status: string
  toEmail: string
  sentAt: string | null
  errorText: string | null
  imapAppendedAt: string | null
  imapError: string | null
  createdAt: string
}
interface Confirmation {
  text: string
  receivedAt: string
}
interface SupplierOrder {
  id: string
  number: string
  status: string
  manufacturerName: string
  manufacturerEmail: string | null
  deliveryAddress: string | null
  expectedDeliveryTime: string | null
  sentAt: string | null
  approvalRequired: boolean
  approvalReason: string | null
  approvedAt: string | null
  items: Item[]
  emails: EmailAttempt[]
  confirmations?: Confirmation[]
  pocztaGotowa: boolean
}

const STATUS_STYLE: Record<string, string> = {
  szkic: 'bg-slate-100 text-slate-600',
  wyslane: 'bg-blue-100 text-blue-700',
  potwierdzone: 'bg-green-100 text-green-700',
  anulowane: 'bg-slate-100 text-slate-400',
}

/** One supplier order (ZP): the email to the maker, the send, the confirmation. */
export function SupplierOrderPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const isOwner = user?.role === 'wlasciciel'
  const [zp, setZp] = useState<SupplierOrder | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function reload() {
    if (!id) return
    api
      .get<{ order: SupplierOrder }>(`/api/supplier-orders/${id}`)
      .then((r) => setZp(r.order))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function approve() {
    if (!zp) return
    setBusy(true)
    try {
      await api.post(`/api/supplier-orders/${zp.id}/approve`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  if (error && !zp) return <div className="p-6 text-red-700">{error}</div>
  if (!zp) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const czekaNaAkceptacje = zp.approvalRequired && !zp.approvedAt
  const magazynBezAkceptacji = czekaNaAkceptacje && !isOwner

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/zamowienia" className="text-sm text-slate-500 hover:text-slate-700">
        ← Zamówienia
      </Link>

      <div className="mt-2 mb-5">
        <h1 className="text-xl font-bold text-slate-800">
          {zp.number}
          <span className={`ml-2 text-xs px-2 py-0.5 rounded align-middle ${STATUS_STYLE[zp.status] ?? ''}`}>
            {zp.status}
          </span>
        </h1>
        <div className="text-sm text-slate-500 mt-0.5">
          do: <strong className="text-slate-700">{zp.manufacturerName}</strong>
          {zp.manufacturerEmail ? ` · ${zp.manufacturerEmail}` : ' · brak adresu e-mail'}
        </div>
      </div>

      {error && (
        <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Warehouse gate — owner's decision, front and centre */}
      {czekaNaAkceptacje && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="font-medium text-amber-900 mb-1">To zamówienie zawiera maszynę na magazyn</div>
          <p className="text-sm text-amber-800 mb-3">
            Zamówienie maszyny bez klienta zamraża gotówkę — wymaga akceptacji właściciela przed wysłaniem.
          </p>
          {isOwner ? (
            <button
              onClick={() => void approve()}
              disabled={busy}
              className="text-sm bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? 'Akceptuję…' : 'Akceptuję zamówienie na magazyn'}
            </button>
          ) : (
            <div className="text-xs text-amber-700">Czeka na akceptację właściciela.</div>
          )}
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        {zp.items.map((it) => (
          <div key={it.id} className={`p-3 border-b border-slate-100 last:border-0 ${it.cancelledAt ? 'opacity-50' : ''}`}>
            <div className="flex justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  {it.model}
                  {it.quantity > 1 && <span className="text-slate-400"> · {it.quantity} szt.</span>}
                </div>
                <div className="text-xs text-slate-400">
                  {it.catalogCode}
                  {it.options.length > 0 && ` · ${it.options.join(', ')}`}
                </div>
              </div>
              <div className="text-right shrink-0 text-xs">
                <div className={it.forCustomer ? 'text-slate-600' : 'text-amber-700 font-medium'}>
                  {it.forCustomer ?? 'na magazyn'}
                </div>
                {it.confirmedDeliveryDate && (
                  <div className="text-slate-500 mt-0.5">
                    dostawa: {new Date(it.confirmedDeliveryDate).toLocaleDateString('pl-PL')}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Before send: the editable email (SPEC 5.3 — always preview and edit) */}
      {!zp.sentAt && !magazynBezAkceptacji && (
        <EmailComposer zp={zp} onSent={reload} />
      )}

      {/* After send: confirmation entry + the send log */}
      {zp.sentAt && (
        <ConfirmationForm zp={zp} onSaved={reload} />
      )}

      {zp.emails.length > 0 && <EmailLog emails={zp.emails} />}

      {/* Owner only: the producer's raw reply, which may carry a purchase price */}
      {isOwner && zp.confirmations && zp.confirmations.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Odpowiedzi producenta (widoczne tylko dla właściciela)</h2>
          {zp.confirmations.map((c, i) => (
            <div key={i} className="text-sm text-slate-600 border-l-2 border-slate-200 pl-3 py-1 mb-1 whitespace-pre-wrap">
              {c.text}
              <div className="text-xs text-slate-400">{dateTime(c.receivedAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Email composer ─────────────────────────────────────────────────────────

function EmailComposer({ zp, onSent }: { zp: SupplierOrder; onSent: () => void }) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [to, setTo] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ subject: string; body: string; to: string | null }>(`/api/supplier-orders/${zp.id}/email-preview`)
      .then((r) => {
        setSubject(r.subject)
        setBody(r.body)
        setTo(r.to)
        setLoaded(true)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }, [zp.id])

  async function send() {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/supplier-orders/${zp.id}/send`, { subject, body })
      onSent()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się wysłać')
    } finally {
      setBusy(false)
    }
  }

  async function markSent() {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/supplier-orders/${zp.id}/mark-sent`, { body })
      onSent()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  if (!loaded) return <div className="text-sm text-slate-400">Przygotowuję treść…</div>

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Wiadomość do producenta</h2>
      <p className="text-xs text-slate-400 mb-3">Sprawdź i popraw treść przed wysłaniem. Nic nie wychodzi automatycznie.</p>

      <label className="block text-xs text-slate-500 mb-1">Do</label>
      <input
        readOnly
        value={to ?? 'brak adresu e-mail producenta'}
        className="w-full px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 text-slate-600 mb-3"
      />

      <label className="block text-xs text-slate-500 mb-1">Temat</label>
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm mb-3 focus:border-brand-600"
      />

      <label className="block text-xs text-slate-500 mb-1">Treść</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={14}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono mb-3 focus:border-brand-600"
      />

      {error && <div className="text-sm text-red-700 mb-3">{error}</div>}

      <div className="flex items-center gap-2">
        {zp.pocztaGotowa ? (
          <button
            onClick={() => void send()}
            disabled={busy || !to}
            className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium"
          >
            {busy ? 'Wysyłam…' : 'Wyślij z CRM'}
          </button>
        ) : (
          <span className="text-xs text-amber-700">
            Skrzynka firmowa nie jest jeszcze skonfigurowana — wyślij z Outlooka i oznacz poniżej.
          </span>
        )}
        <button
          onClick={() => void markSent()}
          disabled={busy}
          className="text-sm border border-slate-300 text-slate-600 px-4 py-2 rounded-md hover:border-brand-600 disabled:opacity-50"
          title="Kopiujesz treść, wysyłasz z własnej poczty i oznaczasz zamówienie jako wysłane"
        >
          Oznacz jako wysłane ręcznie
        </button>
      </div>
    </div>
  )
}

// ── Confirmation form ──────────────────────────────────────────────────────

function ConfirmationForm({ zp, onSaved }: { zp: SupplierOrder; onSaved: () => void }) {
  const [dates, setDates] = useState<Record<string, string>>(() =>
    Object.fromEntries(zp.items.filter((i) => !i.cancelledAt).map((i) => [i.id, i.confirmedDeliveryDate ?? ''])),
  )
  const [rawText, setRawText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const itemDates = Object.entries(dates)
        .filter(([, d]) => d)
        .map(([itemId, date]) => ({ itemId, date }))
      await api.post(`/api/supplier-orders/${zp.id}/confirmation`, { rawText: rawText.trim() || undefined, itemDates })
      setRawText('')
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Potwierdzenie producenta</h2>
      <p className="text-xs text-slate-400 mb-3">
        Wpisz potwierdzone terminy dostaw (osobno dla każdej maszyny) i wklej odpowiedź producenta.
      </p>

      <div className="space-y-2 mb-3">
        {zp.items
          .filter((i) => !i.cancelledAt)
          .map((it) => (
            <div key={it.id} className="flex items-center gap-3">
              <span className="text-sm text-slate-600 flex-1 min-w-0 truncate">
                {it.model} <span className="text-slate-400">· {it.catalogCode}</span>
              </span>
              <input
                type="date"
                value={dates[it.id] ?? ''}
                onChange={(e) => setDates((d) => ({ ...d, [it.id]: e.target.value }))}
                className="px-2 py-1 border border-slate-300 rounded text-sm"
              />
            </div>
          ))}
      </div>

      <label className="block text-xs text-slate-500 mb-1">
        Odpowiedź producenta (wklej całość — widoczna tylko dla właściciela)
      </label>
      <textarea
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        rows={3}
        placeholder="np. confirmed, dostawa week 38"
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3 focus:border-brand-600"
      />

      {error && <div className="text-sm text-red-700 mb-3">{error}</div>}

      <button
        onClick={() => void save()}
        disabled={busy}
        className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium"
      >
        {busy ? 'Zapisuję…' : 'Zapisz potwierdzenie'}
      </button>
    </div>
  )
}

// ── Email log ──────────────────────────────────────────────────────────────

function EmailLog({ emails }: { emails: EmailAttempt[] }) {
  const LABEL: Record<string, string> = {
    wyslany: 'wysłany',
    wyslany_recznie: 'wysłany ręcznie',
    blad: 'błąd',
    w_kolejce: 'w kolejce',
  }
  return (
    <div className="mt-4">
      <h2 className="text-xs uppercase text-slate-400 mb-2">Historia wysyłki</h2>
      <div className="space-y-1">
        {emails.map((e) => (
          <div key={e.id} className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3">
            <span className={e.status === 'blad' ? 'text-red-600' : 'text-slate-600'}>{LABEL[e.status] ?? e.status}</span>
            <span>{dateTime(e.sentAt ?? e.createdAt)}</span>
            {e.errorText && <span className="text-red-600">{e.errorText}</span>}
            {e.status === 'wyslany' && !e.imapAppendedAt && (
              <span className="text-amber-600" title={e.imapError ?? ''}>
                bez kopii w „Wysłanych"
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
